<template>
  <div class="retail-pos-layout">
    <header class="retail-pos-topbar">
      <div class="brand">
        <div v-if="companyLogo" class="brand-logo">
          <img :src="companyLogo" :alt="companyName" />
        </div>
        <div v-else class="brand-mark">
          <i class="fas fa-cloud"></i>
        </div>
        <strong class="brand-name">{{ companyName }}</strong>
      </div>

      <div class="operator-chip">
        <span>{{ operatorName }}</span>
        <i></i>
        <strong>{{ currentDateTime }}</strong>
      </div>

      <div class="topbar-actions">
        <button
          type="button"
          class="topbar-button refresh-button"
          :disabled="refreshDisabled"
          @click="$emit('refresh')"
        >
          <i class="fas fa-sync-alt"></i>
          {{ $t('retailSale.header.refresh') }}
        </button>
        <button
          type="button"
          class="topbar-button customer-display-button"
          :aria-label="$t('retailSale.header.customerDisplay')"
          :title="$t('retailSale.header.customerDisplay')"
          @click="openCustomerDisplay"
        >
          <i class="fas fa-tv"></i>
        </button>
        <div
          v-for="exchange in exchangeRates"
          :key="`${exchange.from_currency_code}-${exchange.to_currency_code}`"
          class="status-chip"
        >
          <i class="fas fa-sync-alt"></i>
          <span>1 {{ exchange.from_currency_symbol }}</span>
          <em>=</em>
          <b>
            {{ formatExchangeRate(exchange.fx_rate) }}
            {{ exchange.to_currency_symbol }}
          </b>
          <small>{{ exchange.to_currency_code }}</small>
        </div>
        <div class="status-chip counter-chip">
          <i class="fas fa-store"></i>
          <span>{{ $t('retailSale.header.counter') }} #001</span>
        </div>
        <div class="language-switch" role="group" :aria-label="$t('language')">
          <button
            v-for="language in languages"
            :key="language.code"
            type="button"
            :class="{ active: language.code === currentLocale }"
            @click="changeLanguage(language)"
          >
            {{ language.caption }}
          </button>
        </div>
        <div class="topbar-divider"></div>
        <button type="button" class="topbar-button" @click="$emit('close-shift')">
          <i class="fas fa-lock"></i>
          {{ $t('retailSale.header.closeShift') }}
        </button>
      </div>
    </header>

    <OrderTabs
      :orders="orders"
      :active-order-id="activeOrderId"
      @change="$emit('change-order', $event)"
      @add="$emit('add-order')"
      @remove="$emit('remove-order', $event)"
    />

    <main class="retail-pos-workspace">
      <section class="product-pane">
        <slot name="products"></slot>
      </section>

      <aside class="cart-pane">
        <slot name="cart"></slot>
      </aside>
    </main>
  </div>
</template>

<script>
import OrderTabs from '../components/order/OrderTabs.vue'
import { formatExchangeRate } from '../utils/currency'
import { createRetailSaleBroadcastChannel } from '../utils/retail-sale'

export default {
  name: 'RetailSalePosLayout',
  components: {
    OrderTabs
  },
  props: {
    orders: {
      type: Array,
      default: () => []
    },
    activeOrderId: {
      type: Number,
      default: null
    },
    exchangeRates: {
      type: Array,
      default: () => []
    },
    refreshDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentDateTime: '',
      clockInterval: null,
      broadcastChannel: null,
      languages: [
        { code: 'kh', caption: 'ខ្មែរ' },
        { code: 'en', caption: 'EN' }
      ]
    }
  },
  created() {
    this.updateCurrentTime()
    this.clockInterval = setInterval(this.updateCurrentTime, 1000)
    this.broadcastChannel = createRetailSaleBroadcastChannel()
  },
  beforeDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval)
      this.clockInterval = null
    }
    if (this.broadcastChannel) this.broadcastChannel.close()
  },
  computed: {
    company() {
      const authUser = this.$store.state.authUser || {}
      const user = authUser.user || {}
      const defaultBranch = user.default_branch || {}

      return defaultBranch.company || {}
    },
    companyLogo() {
      return this.company.company_logo || ''
    },
    companyName() {
      const preferredName =
        this.currentLocale === 'kh'
          ? this.company.company_name_kh
          : this.company.company_name_en

      return (
        preferredName ||
        this.company.company_name_en ||
        this.company.company_name_kh ||
        'POS-CLOUD'
      )
    },
    operatorName() {
      const user = (this.$store.state.authUser || {}).user || {}

      return user.name || user.user_name || ''
    },
    currentLocale() {
      return this.$root.$i18n ? this.$root.$i18n.locale : 'en'
    }
  },
  methods: {
    formatExchangeRate,
    updateCurrentTime() {
      this.currentDateTime = new Date().toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    changeLanguage(language) {
      if (!language || language.code === this.currentLocale) return

      if (!this.$root.$i18n) return

      this.$root.$i18n.locale = language.code
      localStorage.setItem('langCode', language.code)
      document.documentElement.setAttribute('lang', language.code)
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage({
          type: 'language-changed',
          locale: language.code
        })
      }
    },
    async openCustomerDisplay() {
      const route = this.$router.resolve({
        name: 'retail-sale-customer-display'
      })
      const displayWindow = window.open(
        route.href,
        'retail-sale-customer-display',
        'popup=yes,width=1280,height=800,toolbar=no,location=no,menubar=no,status=no,resizable=yes,scrollbars=yes'
      )

      if (!displayWindow) return

      displayWindow.focus()
    }
  }
}
</script>

<style scoped src="../styles/pos-layout.css"></style>
